import Table, { TableBody, TableHeader } from '@ingka/table';
import useScrollSlice from 'hooks/useScrollSlice';
import { useTranslation } from 'react-i18next';
import { memo, useMemo } from 'react';
import useActionPlanContext from 'hooks/useActionPlanContext';
import { ContractChangeType, CoworkerAction, TypeCCDistributionAction, TypeCoworkerActionPlan } from 'types/actionPlan';
import InlineMessage from '@ingka/inline-message';
import { useActionPlan } from 'hooks/api/useActionPlan';
import useData from 'hooks/useData';
import information from '@ingka/ssr-icon/paths/information';
import warningTriangle from '@ingka/ssr-icon/paths/warning-triangle';
import useApp from 'hooks/useApp';
import { OrgSelection } from 'types/appContext';
import { TypeActionPlanAPIResponse } from 'types/api';
import { LoadingWrapper } from 'components/LoadingIndicator/LoadingWrapper';
import Text from '@ingka/text';
import classes from './ActionTable.module.scss';
import { CapacityActionTableRow } from './CapacityActionTableRow';

// Get division codes for coworker's cost centres using hierarchy data
const getDivisionIdForCC = (costCentres: string[], hierarchy: OrgSelection['utils']['hierarchy']) => {
    if (!hierarchy?.costCentreHierarchy) {
        return [];
    }

    const divisionIds: number[] = [];

    hierarchy?.costCentreHierarchy.forEach(({ costCentre, division }) => {
        if (costCentres.includes(costCentre.costCentreCode)) {
            // Prevents duplicate division ids
            if (division?.id !== undefined && !divisionIds.includes(division.id)) {
                divisionIds.push(division.id);
            }
        }
    });

    return divisionIds;
};

const getIsCoworkerSelectedInOMFilters = (
    coworker: TypeCoworkerActionPlan,
    orgSelection: OrgSelection,
) => {
    const { isDummy } = coworker;
    const { costCentreSelection, divisionSelection } = orgSelection.selections;
    const { hierarchy } = orgSelection.utils;

    const selectedDivisionsLength = divisionSelection.filter(({ defaultChecked }) => defaultChecked)?.length ?? 0;
    const selectedCCLength = costCentreSelection.filter(({ defaultChecked }) => defaultChecked)?.length ?? 0;

    const selectedCCs = selectedCCLength
        ? costCentreSelection.filter(({ defaultChecked }) => defaultChecked).map(({ id }) => id) : costCentreSelection?.map(({ id }) => id);
    const selectedDivisions = selectedDivisionsLength
        ? divisionSelection?.filter(({ defaultChecked }) => defaultChecked).map(({ id }) => id) : divisionSelection?.map(({ id }) => id);

    if (isDummy) {
        return coworker.costCentres.some(cc => selectedCCs?.includes(cc));
    }

    const areDivisionsSelected = divisionSelection?.some(({ defaultChecked }) => defaultChecked);
    const areCostCentresSelected = costCentreSelection?.some(({ defaultChecked }) => defaultChecked);

    const divisionIds = getDivisionIdForCC(coworker.costCentres, hierarchy);

    return (areDivisionsSelected ? divisionIds?.some(item => selectedDivisions.includes(item)) : true)
        && (areCostCentresSelected ? coworker.costCentres.some(cc => selectedCCs?.includes(cc)) : true);
};

const getCoworkerFilteredActionPlan = (coworkerActionPlan: TypeCoworkerActionPlan, activeFilter: ContractChangeType | null) => {
    switch (activeFilter) {
    case ContractChangeType.CONTRACT_HOURS:
        // eslint-disable-next-line no-case-declarations
        const contractHoursActionGroups = coworkerActionPlan.actionGroups.map(actionGroup => ({
            ...actionGroup,
            actions: actionGroup.actions
                .filter(action => action.type !== CoworkerAction.MODULATION)
        })).filter(g => g.actions.length);

        if (!contractHoursActionGroups.length) {
            return null;
        }

        return {
            ...coworkerActionPlan,
            actionGroups: contractHoursActionGroups
        };
    case ContractChangeType.MODULATION:
        // eslint-disable-next-line no-case-declarations
        const modulationActionGroups = coworkerActionPlan.actionGroups.map(actionGroup => ({
            ...actionGroup,
            actions: actionGroup.actions
                .filter(action => action.type === CoworkerAction.MODULATION)
        })).filter(g => g.actions.length);

        if (!modulationActionGroups.length) {
            return null;
        }

        return {
            ...coworkerActionPlan,
            actionGroups: modulationActionGroups
        };
    default:
        return coworkerActionPlan;
    }
};

const CapacityActionTable = memo(() => {
    const { t } = useTranslation();
    const { activeFilter } = useActionPlanContext();
    const { currentScenario } = useData();
    const { data, error, isLoading } = useActionPlan();
    const { searchParam, orgSelection } = useApp();
    const { slice } = useScrollSlice(30, data?.length, 'capacity-action-plan-page');

    const coworkersFilteredActionPlan = useMemo(
        () => {
            if (!data || !currentScenario || error) {
                return null;
            }

            const coworkers = data?.map(coworker => {
                const ccDistributionAction: TypeCCDistributionAction[] = [];
                coworker.actionGroups.forEach(({ actions }) => {
                    actions.forEach(action => {
                        if (action.type === CoworkerAction.CC_DISTRIBUTION) {
                            ccDistributionAction.push(action);
                        }
                    });
                });

                ccDistributionAction.forEach(({ ccDistributions }) => {
                    ccDistributions.forEach(({ from, to }) => {
                        if (!coworker.costCentres.includes(from.costCentre)) {
                            coworker.costCentres.push(from.costCentre);
                        }
                        if (!coworker.costCentres.includes(to.costCentre)) {
                            coworker.costCentres.push(to.costCentre);
                        }
                    });
                });

                if (searchParam
                    && !coworker.name.toLocaleLowerCase().includes(searchParam.trim().toLocaleLowerCase())
                    && !coworker.personId.includes(searchParam.trim().toLocaleLowerCase())) {
                    return null;
                }

                if (!getIsCoworkerSelectedInOMFilters(coworker, orgSelection)) {
                    return null;
                }

                return getCoworkerFilteredActionPlan(coworker, activeFilter);
            }).filter(el => !!el);

            return coworkers;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data, activeFilter, searchParam, orgSelection, currentScenario]
    );

    if (error) {
        return (
            <div>
                <InlineMessage ssrIcon={warningTriangle} title={t('GENERIC_ERROR')} variant="informative" />
            </div>
        );
    }

    if (!isLoading && !coworkersFilteredActionPlan?.length) {
        return (
            <div>
                <InlineMessage
                    ssrIcon={information}
                    title={!currentScenario ? t('COWORKERS_SELECT_SCENARIO') : t('NO_PERSONNEL_DATA')}
                    variant="informative"
                />
            </div>
        );
    }

    return (
        <LoadingWrapper isLoading={isLoading}>
            <Table id="capacity-action-table" data-testid="capacity-action-table" fullWidth className={isLoading ? classes.table__loading : ''}>
                <TableHeader sticky>
                    <tr>
                        <th><Text textStyle="HeadingRegular.XS" data-testid="capacity-action-table-coworker">{t('COWORKER')}</Text></th>
                        <th><Text textStyle="HeadingRegular.XS" data-testid="capacity-action-table-contract-changes">{t('CONTRACT_CHANGES')}</Text></th>
                        <th><Text textStyle="HeadingRegular.XS" data-testid="capacity-action-table-last-changed">{t('LAST_CHANGED')}</Text></th>
                        <th><Text textStyle="HeadingRegular.XS" data-testid="capacity-action-table-cost-centre">{t('COST_CENTRE')}</Text></th>
                    </tr>
                </TableHeader>
                <TableBody>
                    {
                        (coworkersFilteredActionPlan as TypeActionPlanAPIResponse)
                            ?.slice(slice.start, slice.end)
                            .map(coworkerActionPlan => (
                                <CapacityActionTableRow
                                    key={`coworker-rows-${coworkerActionPlan?.personId}`}
                                    coworkerActionPlan={coworkerActionPlan}
                                />
                            ))
                    }
                </TableBody>
            </Table>
        </LoadingWrapper>
    );
});

export default CapacityActionTable;
